import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail, CheckCircle } from 'lucide-react';
import { subscribeNewsletter, getCategories } from '../services/api';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      if (response.success) {
        setCategories(response.data.slice(0, 5)); // Take only first 5 categories
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage('Please enter your email address');
      setSuccess(false);
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage('Please enter a valid email address');
      setSuccess(false);
      return;
    }
    
    setSubscribing(true);
    setMessage('');
    
    try {
      const response = await subscribeNewsletter(email, '', 'footer');
      
      if (response.success) {
        setMessage(response.message);
        setSuccess(true);
        setEmail('');
        
        setTimeout(() => {
          setMessage('');
        }, 5000);
      } else {
        setMessage(response.message || 'Subscription failed. Please try again.');
        setSuccess(false);
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setMessage('Network error. Please try again.');
      setSuccess(false);
    } finally {
      setSubscribing(false);
    }
  };
  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">BC</span>
              </div>
              <span className="text-2xl font-bold text-white">BlogCraft</span>
            </div>
            <p className="text-gray-400 mb-4">
              Your source for quality content on technology, lifestyle, business, and more.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="hover:text-blue-500 transition">
                <Facebook size={20} />
              </a>
              <a href="#" className="hover:text-blue-400 transition">
                <Twitter size={20} />
              </a>
              <a href="#" className="hover:text-pink-500 transition">
                <Instagram size={20} />
              </a>
              <a href="#" className="hover:text-blue-600 transition">
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-white font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="hover:text-white transition">Home</Link></li>
              <li><Link to="/blog" className="hover:text-white transition">Blog</Link></li>
              <li><Link to="/about" className="hover:text-white transition">About Us</Link></li>
              <li><Link to="/contact" className="hover:text-white transition">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold mb-4">Categories</h3>
            <ul className="space-y-2">
              {categories.length > 0 ? (
                categories.map((category) => (
                  <li key={category.id}>
                    <Link 
                      to={`/category/${category.slug}`} 
                      className="hover:text-white transition"
                    >
                      {category.name}
                    </Link>
                  </li>
                ))
              ) : (
                <li><span className="text-gray-500">Loading categories...</span></li>
              )}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-bold mb-4">Newsletter</h3>
            <p className="text-gray-400 mb-4">Subscribe to get the latest posts delivered to your inbox.</p>
            
            {message && (
              <div className={`mb-4 p-3 rounded-lg flex items-center space-x-2 ${
                success ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
              }`}>
                {success ? <CheckCircle size={16} /> : <Mail size={16} />}
                <span className="text-sm">{message}</span>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="flex flex-col space-y-2">
              <div className="flex">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="flex-1 px-4 py-2 rounded-l-lg bg-gray-800 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={subscribing}
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-r-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {subscribing ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <Mail size={20} />
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} BlogCraft. All rights reserved. Built with ❤️ for content creators.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
